// Shared 3D character builder. Outputs a runner figure that visually matches
// the in-game `Player` exactly so the same character is shown in:
//   • the main-menu lobby podium (MainMenuHero)
//   • the profile / avatar viewer (AvatarViewer3D)
//   • the single-player end-of-level podium (PodiumScene)
//   • the multiplayer end-of-race podium (BattlePodiumScene)
//
// The in-game `Player.ts` keeps its own copy of this geometry (it has
// real-time animation hooks bound to specific parts), but the geometry and
// material rules here are identical so the look is consistent across screens.

import * as THREE from "three";
import type { AvatarConfig, HatId, PatternId } from "@shared/types";
import { SoftBox } from "./roundedBoxes";

export interface RunnerParts {
  /** Top-level group — add this to your scene at the position you want. */
  group: THREE.Group;
  /** Sub-group holding torso/face/eyes/hat — useful for body-bob animation. */
  body: THREE.Group;
  /** Left and right leg pivots — rotate `.x` for a run cycle. */
  leftLeg: THREE.Group;
  rightLeg: THREE.Group;
}

export interface RunnerOptions {
  /** Overall world scale factor applied to the returned group.
   *  Default 1 — most preview scenes want this. The in-game player uses
   *  0.25 internally and isn't built through this function. */
  scale?: number;
}

/** Build a runner character from an AvatarConfig (shirt color, pattern, hat,
 *  skin, pants). Returns the group + animation handles. */
export function buildRunnerCharacter(avatar: AvatarConfig, opts: RunnerOptions = {}): RunnerParts {
  const suit = new THREE.Color(avatar.shirtColor ?? "#ff2ca4");
  const faceColor = new THREE.Color(avatar.skinColor ?? "#ffd6ef").lerp(
    new THREE.Color("#ffffff"),
    0.34,
  );
  const footColor = new THREE.Color(avatar.pantsColor ?? avatar.shirtColor ?? "#e11d8a").lerp(
    new THREE.Color("#111827"),
    0.12,
  );

  const group = new THREE.Group();
  const body = new THREE.Group();
  group.add(body);

  // Torso — egg-shaped sphere with the procedural shirt pattern texture.
  const torsoMat = makeShirtMaterial(suit, avatar.patternId ?? "solid");
  // upgrade #5 — Fresnel rim light on the player character. Adds a soft
  // bright edge along silhouettes so the runner reads as "expensive
  // plastic" instead of matte. Cheap: ~5 extra GLSL ops per fragment.
  applyFresnelRim(torsoMat, new THREE.Color(0xffffff), 1.4);
  const torso = new THREE.Mesh(
    new THREE.SphereGeometry(0.92, 32, 32),
    torsoMat,
  );
  torso.scale.set(0.9, 1.34, 0.68);
  torso.position.y = 1.62;
  torso.castShadow = true;
  torso.receiveShadow = true;
  body.add(torso);

  // Face — flat oval panel on the front, slightly indented from the torso.
  const faceMat = new THREE.MeshStandardMaterial({ color: faceColor, roughness: 0.5 });
  applyFresnelRim(faceMat, new THREE.Color(0xffffff), 1.0);
  const face = new THREE.Mesh(
    new THREE.SphereGeometry(0.38, 24, 20),
    faceMat,
  );
  face.scale.set(1.05, 1.18, 0.15);
  face.position.set(0, 2.05, 0.63);
  body.add(face);

  // Eye slits.
  const eyeMat = new THREE.MeshBasicMaterial({ color: 0x111111 });
  for (const dx of [-0.15, 0.15]) {
    const eye = new THREE.Mesh(new THREE.CapsuleGeometry(0.035, 0.12, 6, 12), eyeMat);
    eye.position.set(dx, 2.07, 0.7);
    body.add(eye);
  }

  // Hat (optional).
  const hatId = (avatar.hatId ?? "none") as HatId;
  if (hatId !== "none") body.add(buildHat(hatId));
  // Outfit overlay sits on the torso (jacket / apron / cape / etc.)
  const outfitId = (avatar.outfitId ?? "none") as string;
  if (outfitId !== "none") body.add(buildOutfit(outfitId));
  // Face accessory (glasses, mustache, etc.) attaches to the head.
  const faceId = (avatar.faceId ?? "none") as string;
  if (faceId !== "none") body.add(buildFace(faceId));

  // Stubby legs — pivots so the caller can rotate them for a run cycle.
  const leg = (x: number) => {
    const pivot = new THREE.Group();
    pivot.position.set(x, 0.58, 0.1);
    const shin = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.18, 0.5, 16, 24),
      new THREE.MeshStandardMaterial({ color: suit, roughness: 0.58, metalness: 0.03 }),
    );
    shin.position.y = -0.3;
    shin.castShadow = true;
    shin.receiveShadow = true;
    pivot.add(shin);
    const foot = new THREE.Mesh(
      new THREE.SphereGeometry(0.22, 18, 14),
      new THREE.MeshStandardMaterial({ color: footColor, roughness: 0.62, metalness: 0.02 }),
    );
    foot.scale.set(0.95, 0.62, 1.35);
    foot.position.set(0, -0.64, 0.15);
    foot.castShadow = true;
    pivot.add(foot);
    body.add(pivot);
    return pivot;
  };
  const leftLeg = leg(-0.28);
  const rightLeg = leg(0.28);

  if (opts.scale && opts.scale !== 1) {
    group.scale.setScalar(opts.scale);
  }
  return { group, body, leftLeg, rightLeg };
}

/** Default idle-pose breathing animation. Caller passes elapsed seconds.
 *  Convenience helper for static preview scenes — game uses its own. */
export function animateRunnerIdle(parts: RunnerParts, tSeconds: number, jumpT = 1) {
  const jumping = jumpT < 1;
  const bounce = Math.abs(Math.sin(tSeconds * 3.2)) * 0.16;
  const breath = Math.sin(tSeconds * 2.1) * 0.035;
  parts.body.position.y = jumping ? bounce * 0.24 : breath * 0.35;
  parts.body.rotation.x = jumping ? -0.1 - Math.sin(jumpT * Math.PI) * 0.18 : 0;
  parts.body.scale.set(1 - breath * 0.12, 1 + breath, 1 - breath * 0.12);
  if (jumping) {
    parts.leftLeg.rotation.x = -0.95 + jumpT * 0.35;
    parts.rightLeg.rotation.x = -0.95 + jumpT * 0.35;
  } else {
    parts.leftLeg.rotation.x = 0;
    parts.rightLeg.rotation.x = 0;
  }
}

// ── Internal: hat builder (mirrors Player.ts buildHat exactly) ───────────
export function buildHat(id: HatId): THREE.Object3D {
  const g = new THREE.Group();
  switch (id) {
    case "cap-blue":
    case "cap-red":
    case "cap-green":
    case "cap-purple": {
      const colorMap: Record<string, number> = {
        "cap-blue": 0x2563eb,
        "cap-red": 0xdc2626,
        "cap-green": 0x16a34a,
        "cap-purple": 0x9b5cf6,
      };
      const color = colorMap[id]!;
      const mat = new THREE.MeshStandardMaterial({ color });
      const crown = new THREE.Mesh(new SoftBox(0.78, 0.32, 0.78), mat);
      crown.position.set(0, 2.92, 0);
      crown.castShadow = true;
      g.add(crown);
      const brim = new THREE.Mesh(new SoftBox(0.78, 0.08, 0.45), mat);
      brim.position.set(0, 2.78, 0.5);
      brim.castShadow = true;
      g.add(brim);
      break;
    }
    case "beanie":
    case "beanie-blue": {
      const color = id === "beanie-blue" ? 0x2563eb : 0xef476f;
      const beanie = new THREE.Mesh(
        new SoftBox(0.82, 0.4, 0.82),
        new THREE.MeshStandardMaterial({ color }),
      );
      beanie.position.set(0, 2.96, 0);
      beanie.castShadow = true;
      g.add(beanie);
      const pom = new THREE.Mesh(
        new THREE.SphereGeometry(0.12, 8, 8),
        new THREE.MeshStandardMaterial({ color: 0xffffff }),
      );
      pom.position.set(0, 3.22, 0);
      pom.castShadow = true;
      g.add(pom);
      break;
    }
    case "top-hat": {
      const black = new THREE.MeshStandardMaterial({ color: 0x111827, roughness: 0.4 });
      const brim = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.62, 0.08, 24), black);
      brim.position.y = 2.78;
      brim.castShadow = true;
      g.add(brim);
      const stack = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.4, 0.7, 24), black);
      stack.position.y = 3.17;
      stack.castShadow = true;
      g.add(stack);
      const band = new THREE.Mesh(
        new THREE.CylinderGeometry(0.41, 0.41, 0.1, 24),
        new THREE.MeshStandardMaterial({ color: 0xff2ca4, roughness: 0.4 }),
      );
      band.position.y = 2.88;
      g.add(band);
      break;
    }
    case "wizard": {
      const purple = new THREE.MeshStandardMaterial({ color: 0x6d28d9, roughness: 0.5 });
      const cone = new THREE.Mesh(new THREE.ConeGeometry(0.5, 1.15, 16), purple);
      cone.position.y = 3.32;
      cone.castShadow = true;
      g.add(cone);
      // Gold stars stuck to the cone
      const goldMat = new THREE.MeshStandardMaterial({ color: 0xffd166, emissive: 0xffb020, emissiveIntensity: 0.4 });
      for (let k = 0; k < 5; k++) {
        const star = new THREE.Mesh(new THREE.IcosahedronGeometry(0.07, 0), goldMat);
        const ang = (k / 5) * Math.PI * 2;
        const yT = 0.2 + (k % 2) * 0.35;
        const r = 0.46 - yT * 0.4;
        star.position.set(Math.sin(ang) * r, 2.85 + yT, Math.cos(ang) * r);
        g.add(star);
      }
      // Wide brim at base
      const brim = new THREE.Mesh(
        new THREE.CylinderGeometry(0.7, 0.7, 0.08, 24),
        purple,
      );
      brim.position.y = 2.78;
      brim.castShadow = true;
      g.add(brim);
      break;
    }
    case "horns": {
      const redMat = new THREE.MeshStandardMaterial({ color: 0xdc2626, roughness: 0.42, metalness: 0.05 });
      for (const x of [-0.22, 0.22]) {
        const horn = new THREE.Mesh(new THREE.ConeGeometry(0.13, 0.45, 14), redMat);
        horn.position.set(x, 2.92, -0.05);
        horn.rotation.z = x < 0 ? 0.3 : -0.3;
        horn.castShadow = true;
        g.add(horn);
      }
      break;
    }
    case "headband": {
      const band = new THREE.Mesh(
        new SoftBox(0.88, 0.16, 0.7),
        new THREE.MeshStandardMaterial({ color: 0xef4444 }),
      );
      band.position.y = 2.72;
      band.castShadow = true;
      g.add(band);
      // Knot at the back
      const knot = new THREE.Mesh(
        new THREE.SphereGeometry(0.12, 14, 10),
        new THREE.MeshStandardMaterial({ color: 0xef4444 }),
      );
      knot.position.set(0, 2.75, -0.42);
      g.add(knot);
      break;
    }
    case "bow": {
      const pinkMat = new THREE.MeshStandardMaterial({ color: 0xff5fa7, roughness: 0.4 });
      // Two loops on either side of a central knot.
      for (const x of [-0.18, 0.18]) {
        const loop = new THREE.Mesh(
          new THREE.SphereGeometry(0.16, 16, 12),
          pinkMat,
        );
        loop.scale.set(1, 0.7, 0.5);
        loop.position.set(x, 2.78, 0.18);
        loop.castShadow = true;
        g.add(loop);
      }
      const knot = new THREE.Mesh(
        new THREE.SphereGeometry(0.08, 12, 10),
        pinkMat,
      );
      knot.position.set(0, 2.78, 0.18);
      g.add(knot);
      break;
    }
    case "crown": {
      const goldMat = new THREE.MeshStandardMaterial({ color: 0xffd166, metalness: 0.6, roughness: 0.3 });
      const band = new THREE.Mesh(new SoftBox(0.78, 0.18, 0.78), goldMat);
      band.position.set(0, 2.85, 0);
      band.castShadow = true;
      g.add(band);
      for (let i = -1; i <= 1; i++) {
        for (let j = -1; j <= 1; j++) {
          if (Math.abs(i) + Math.abs(j) !== 1) continue;
          const spike = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.3, 4), goldMat);
          spike.position.set(i * 0.3, 3.05, j * 0.3);
          g.add(spike);
        }
      }
      break;
    }
    case "party-hat": {
      const cone = new THREE.Mesh(
        new THREE.ConeGeometry(0.42, 0.95, 16),
        new THREE.MeshStandardMaterial({ color: 0xa855f7, roughness: 0.45 }),
      );
      cone.position.set(0, 3.18, 0);
      cone.castShadow = true;
      g.add(cone);
      for (let k = 0; k < 8; k++) {
        const dot = new THREE.Mesh(
          new THREE.SphereGeometry(0.06, 8, 6),
          new THREE.MeshStandardMaterial({ color: 0xffd166 }),
        );
        const ang = (k / 8) * Math.PI * 2;
        const yT = 0.2 + (k % 3) * 0.22;
        const r = 0.42 - yT * 0.42;
        dot.position.set(Math.sin(ang) * r, 2.8 + yT, Math.cos(ang) * r);
        g.add(dot);
      }
      const tip = new THREE.Mesh(
        new THREE.SphereGeometry(0.13, 10, 8),
        new THREE.MeshStandardMaterial({ color: 0xffd166 }),
      );
      tip.position.set(0, 3.7, 0);
      g.add(tip);
      break;
    }
    case "halo": {
      const halo = new THREE.Mesh(
        new THREE.TorusGeometry(0.42, 0.08, 12, 28),
        new THREE.MeshStandardMaterial({
          color: 0xfef08a,
          emissive: 0xfff3a8,
          emissiveIntensity: 0.45,
          metalness: 0.3,
          roughness: 0.25,
        }),
      );
      halo.rotation.x = Math.PI / 2;
      halo.position.set(0, 3.18, 0);
      g.add(halo);
      break;
    }
    // ── v2 hats ────────────────────────────────────────────────────────
    case "chef": {
      const cylMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.5 });
      const band = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.42, 0.22, 18), cylMat);
      band.position.y = 2.72; g.add(band);
      const puff = new THREE.Mesh(new THREE.SphereGeometry(0.5, 18, 14), cylMat);
      puff.position.y = 3.05; puff.scale.y = 0.95; g.add(puff);
      break;
    }
    case "cowboy": {
      const mat = new THREE.MeshStandardMaterial({ color: 0x8b5a2b, roughness: 0.6 });
      const crown = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.36, 0.4, 16), mat);
      crown.position.y = 2.85; g.add(crown);
      const brim = new THREE.Mesh(new THREE.CylinderGeometry(0.8, 0.85, 0.08, 28), mat);
      brim.position.y = 2.66; g.add(brim);
      const accent = new THREE.Mesh(
        new THREE.TorusGeometry(0.34, 0.04, 8, 18),
        new THREE.MeshStandardMaterial({ color: 0xffd166, metalness: 0.4, roughness: 0.3 }),
      );
      accent.rotation.x = Math.PI / 2; accent.position.y = 2.7; g.add(accent);
      break;
    }
    case "propeller": {
      const cap = new THREE.Mesh(
        new THREE.SphereGeometry(0.4, 18, 14, 0, Math.PI * 2, 0, Math.PI / 2),
        new THREE.MeshStandardMaterial({ color: 0xef4444, roughness: 0.45 }),
      );
      cap.position.y = 2.62; g.add(cap);
      // Propeller stem.
      const stem = new THREE.Mesh(
        new THREE.CylinderGeometry(0.04, 0.04, 0.25, 8),
        new THREE.MeshStandardMaterial({ color: 0x444444 }),
      );
      stem.position.y = 3.12; g.add(stem);
      // Two blades.
      const bladeMat = new THREE.MeshStandardMaterial({ color: 0xfde047, roughness: 0.4 });
      const bladeGeo = new THREE.BoxGeometry(0.55, 0.06, 0.12);
      const bladeA = new THREE.Mesh(bladeGeo, bladeMat); bladeA.position.y = 3.28; g.add(bladeA);
      const bladeB = new THREE.Mesh(bladeGeo, bladeMat); bladeB.position.y = 3.28; bladeB.rotation.y = Math.PI / 2; g.add(bladeB);
      break;
    }
    case "bunny-ears": {
      const mat = new THREE.MeshStandardMaterial({ color: 0xfde2f3, roughness: 0.55 });
      const innerMat = new THREE.MeshStandardMaterial({ color: 0xff8fb8, roughness: 0.5 });
      for (const x of [-0.22, 0.22]) {
        const ear = new THREE.Mesh(new THREE.CapsuleGeometry(0.12, 0.55, 14, 14), mat);
        ear.position.set(x, 3.05, 0); ear.rotation.z = x < 0 ? 0.16 : -0.16; g.add(ear);
        const inner = new THREE.Mesh(new THREE.CapsuleGeometry(0.06, 0.4, 14, 14), innerMat);
        inner.position.set(x, 3.08, 0.05); inner.rotation.z = ear.rotation.z; g.add(inner);
      }
      break;
    }
    case "fox-ears": {
      const mat = new THREE.MeshStandardMaterial({ color: 0xff7a3a, roughness: 0.55 });
      const tipMat = new THREE.MeshStandardMaterial({ color: 0x1f2937, roughness: 0.6 });
      for (const x of [-0.26, 0.26]) {
        const ear = new THREE.Mesh(new THREE.ConeGeometry(0.18, 0.45, 14), mat);
        ear.position.set(x, 2.92, 0); ear.rotation.z = x < 0 ? 0.18 : -0.18; g.add(ear);
        const tip = new THREE.Mesh(new THREE.ConeGeometry(0.07, 0.16, 12), tipMat);
        tip.position.set(x, 3.12, 0); tip.rotation.z = ear.rotation.z; g.add(tip);
      }
      break;
    }
    case "pirate": {
      const mat = new THREE.MeshStandardMaterial({ color: 0x1f2937, roughness: 0.55 });
      const band = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.45, 0.22, 18), mat);
      band.position.y = 2.7; g.add(band);
      // Pointed flaps on either side (tricorne flair).
      const flap = new THREE.Mesh(new THREE.ConeGeometry(0.55, 0.32, 4, 1, false, 0, Math.PI), mat);
      flap.position.y = 2.92; flap.rotation.x = Math.PI / 2; g.add(flap);
      // Skull-and-crossbones dot.
      const skull = new THREE.Mesh(
        new THREE.SphereGeometry(0.08, 12, 10),
        new THREE.MeshStandardMaterial({ color: 0xffffff }),
      );
      skull.position.set(0, 2.78, 0.45); g.add(skull);
      break;
    }
    case "viking": {
      const mat = new THREE.MeshStandardMaterial({ color: 0x9ca3af, metalness: 0.55, roughness: 0.35 });
      const helm = new THREE.Mesh(new THREE.SphereGeometry(0.45, 18, 14, 0, Math.PI * 2, 0, Math.PI / 2), mat);
      helm.position.y = 2.65; g.add(helm);
      const hornMat = new THREE.MeshStandardMaterial({ color: 0xe5e7eb, roughness: 0.45 });
      for (const x of [-0.45, 0.45]) {
        const horn = new THREE.Mesh(new THREE.ConeGeometry(0.11, 0.45, 12), hornMat);
        horn.position.set(x, 2.92, 0);
        horn.rotation.z = x < 0 ? 0.7 : -0.7;
        g.add(horn);
      }
      break;
    }
    case "sun-hat": {
      const mat = new THREE.MeshStandardMaterial({ color: 0xfde68a, roughness: 0.6 });
      const ribbon = new THREE.MeshStandardMaterial({ color: 0xff5fa7, roughness: 0.5 });
      const brim = new THREE.Mesh(new THREE.CylinderGeometry(0.95, 1.0, 0.06, 32), mat);
      brim.position.y = 2.6; g.add(brim);
      const dome = new THREE.Mesh(new THREE.SphereGeometry(0.36, 18, 14, 0, Math.PI * 2, 0, Math.PI / 2), mat);
      dome.position.y = 2.6; g.add(dome);
      const band = new THREE.Mesh(new THREE.CylinderGeometry(0.37, 0.37, 0.1, 24), ribbon);
      band.position.y = 2.66; g.add(band);
      break;
    }
  }
  return g;
}

// ── Internal: outfit overlay (jacket, apron, cape, etc.) ─────────────────
/** Build an egg-shape that wraps the runner's torso. The runner torso
 *  is a sphere(0.92) scaled (0.9, 1.34, 0.68) at y=1.62 — copying the
 *  same shape ~3% larger gives outfits that hug the body cleanly
 *  instead of intersecting it like the old rigid cylinders. */
function buildTorsoShell(mat: THREE.Material, scale = 1.03): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.SphereGeometry(0.92, 28, 22), mat);
  m.scale.set(0.9 * scale, 1.34 * scale, 0.68 * scale);
  m.position.y = 1.62;
  return m;
}

function buildOutfit(id: string): THREE.Object3D {
  const g = new THREE.Group();
  switch (id) {
    case "jacket": {
      const mat = new THREE.MeshStandardMaterial({ color: 0x2563eb, roughness: 0.5 });
      // Wrap the torso shape rather than clipping a rigid cylinder
      // through it. Bottom half only by clipping with a mask plane
      // wouldn't work cleanly in vanilla three, so we slice via
      // geometry parameters: build the shell, then cut a V neckline
      // by hiding a wedge of faces via a darker collar mesh.
      g.add(buildTorsoShell(mat, 1.035));
      // V-collar accent strip.
      const collar = new THREE.Mesh(
        new THREE.RingGeometry(0.38, 0.5, 24, 1, 0, Math.PI),
        new THREE.MeshStandardMaterial({ color: 0x1d4ed8, side: THREE.DoubleSide, roughness: 0.55 }),
      );
      collar.rotation.x = -Math.PI / 2;
      collar.position.set(0, 2.05, 0.0);
      g.add(collar);
      // Two buttons down the front.
      const bMat = new THREE.MeshStandardMaterial({ color: 0xfde047, metalness: 0.35, roughness: 0.35 });
      for (const y of [1.65, 1.35, 1.05]) {
        const btn = new THREE.Mesh(new THREE.SphereGeometry(0.05, 14, 10), bMat);
        btn.position.set(0, y, 0.61);
        g.add(btn);
      }
      break;
    }
    case "hoodie": {
      const mat = new THREE.MeshStandardMaterial({ color: 0x6b7280, roughness: 0.65 });
      g.add(buildTorsoShell(mat, 1.04));
      // Hood lump behind the head — sphere wrapped around the upper back.
      const hood = new THREE.Mesh(new THREE.SphereGeometry(0.48, 18, 14), mat);
      hood.position.set(0, 2.32, -0.22); hood.scale.set(1, 0.9, 0.85); g.add(hood);
      // Front pocket — curved patch on the belly.
      const pocketMat = new THREE.MeshStandardMaterial({ color: 0x4b5563, roughness: 0.6 });
      const pocket = new THREE.Mesh(new THREE.SphereGeometry(0.5, 16, 12), pocketMat);
      pocket.scale.set(1.0, 0.42, 0.18);
      pocket.position.set(0, 1.3, 0.62);
      g.add(pocket);
      // Drawstrings dangling under the neckline.
      const stringMat = new THREE.MeshStandardMaterial({ color: 0xe5e7eb });
      for (const x of [-0.08, 0.08]) {
        const s = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.32, 6), stringMat);
        s.position.set(x, 1.92, 0.6);
        g.add(s);
      }
      break;
    }
    case "apron": {
      const mat = new THREE.MeshStandardMaterial({ color: 0xff5fa7, roughness: 0.55 });
      const front = new THREE.Mesh(new THREE.BoxGeometry(0.85, 1.1, 0.08), mat);
      front.position.set(0, 1.5, 0.48); g.add(front);
      // Strap.
      const strap = new THREE.Mesh(
        new THREE.TorusGeometry(0.45, 0.05, 8, 24, Math.PI),
        mat,
      );
      strap.position.set(0, 2.0, 0); strap.rotation.x = -Math.PI / 2; g.add(strap);
      // Pocket.
      const pocket = new THREE.Mesh(
        new THREE.BoxGeometry(0.6, 0.2, 0.05),
        new THREE.MeshStandardMaterial({ color: 0xffd1dc }),
      );
      pocket.position.set(0, 1.2, 0.53); g.add(pocket);
      break;
    }
    case "vest": {
      const mat = new THREE.MeshStandardMaterial({ color: 0xfde047, roughness: 0.5 });
      const front = new THREE.Mesh(new THREE.BoxGeometry(0.75, 1.0, 0.1), mat);
      front.position.set(0, 1.55, 0.42); g.add(front);
      // Two buttons.
      const bMat = new THREE.MeshStandardMaterial({ color: 0x1f2937, metalness: 0.3 });
      for (const y of [1.75, 1.45, 1.15]) {
        const btn = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.04, 12), bMat);
        btn.position.set(0, y, 0.49); btn.rotation.x = Math.PI / 2; g.add(btn);
      }
      break;
    }
    case "cape": {
      const mat = new THREE.MeshStandardMaterial({ color: 0xc41d80, roughness: 0.5 });
      const cape = new THREE.Mesh(new THREE.PlaneGeometry(0.95, 1.4), mat);
      cape.material.side = THREE.DoubleSide;
      cape.position.set(0, 1.5, -0.35);
      cape.rotation.x = -0.05;
      g.add(cape);
      // Clasp at neck.
      const clasp = new THREE.Mesh(
        new THREE.SphereGeometry(0.08, 14, 12),
        new THREE.MeshStandardMaterial({ color: 0xffd166, metalness: 0.6 }),
      );
      clasp.position.set(0, 2.18, -0.18); g.add(clasp);
      break;
    }
    case "robe": {
      const mat = new THREE.MeshStandardMaterial({ color: 0x6d28d9, roughness: 0.55 });
      // Slightly larger shell so the robe drapes the body.
      g.add(buildTorsoShell(mat, 1.07));
      // Belt rope around the body — match the egg's lower waist circumference.
      const belt = new THREE.Mesh(
        new THREE.TorusGeometry(0.7, 0.07, 12, 32),
        new THREE.MeshStandardMaterial({ color: 0xffd166, roughness: 0.45, metalness: 0.3 }),
      );
      belt.rotation.x = Math.PI / 2;
      belt.position.y = 1.32;
      belt.scale.set(1, 1, 0.72); // squash to match the torso's z-flatness
      g.add(belt);
      break;
    }
    case "scarf": {
      const mat = new THREE.MeshStandardMaterial({ color: 0xef4444, roughness: 0.5 });
      // Tube wrapping around the neck — built as a torus shaped to the
      // body's elliptical cross-section so it actually hugs the runner.
      const wrap = new THREE.Mesh(
        new THREE.TorusGeometry(0.42, 0.12, 14, 36),
        mat,
      );
      wrap.rotation.x = Math.PI / 2;
      wrap.position.y = 2.05;
      wrap.scale.set(1, 1, 0.78); // egg-shape squash
      g.add(wrap);
      // Two scarf tails hanging down the front.
      for (const x of [-0.12, 0.18]) {
        const tail = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.7, 0.06), mat);
        tail.position.set(x, 1.62, 0.55);
        tail.rotation.z = x < 0 ? 0.08 : -0.12;
        g.add(tail);
      }
      break;
    }
  }
  // Same depth/render fix as buildFace — outfits sit just outside the
  // torso surface and Safari z-fights them otherwise.
  g.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    const mat = m.material as THREE.Material & { polygonOffset?: boolean; polygonOffsetFactor?: number; polygonOffsetUnits?: number };
    mat.polygonOffset = true;
    mat.polygonOffsetFactor = -2;
    mat.polygonOffsetUnits = -2;
    m.renderOrder = 3;
  });
  return g;
}

// ── Internal: face accessory (glasses, mustache, etc.) ───────────────────
function buildFace(id: string): THREE.Object3D {
  const g = new THREE.Group();
  // Ensure every face-accessory mesh renders on top of the head sphere.
  // Without polygonOffset + a high renderOrder Safari z-fights the
  // glasses against the face panel and the lenses pop in/out per frame.
  const tweak = (mesh: THREE.Mesh) => {
    const mat = mesh.material as THREE.Material & { polygonOffset?: boolean; polygonOffsetFactor?: number; polygonOffsetUnits?: number; depthTest?: boolean };
    mat.polygonOffset = true;
    mat.polygonOffsetFactor = -4;
    mat.polygonOffsetUnits = -4;
    mesh.renderOrder = 5;
    return mesh;
  };
  const G = (m: THREE.Mesh) => { tweak(m); g.add(m); };
  switch (id) {
    case "glasses": {
      const frameMat = new THREE.MeshStandardMaterial({ color: 0x1f2937, roughness: 0.4 });
      for (const x of [-0.15, 0.15]) {
        const rim = new THREE.Mesh(new THREE.TorusGeometry(0.12, 0.025, 8, 18), frameMat);
        rim.rotation.y = Math.PI / 2; rim.position.set(x, 2.13, 0.78); g.add(rim);
      }
      const bridge = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.1, 8), frameMat);
      bridge.rotation.z = Math.PI / 2; bridge.position.set(0, 2.13, 0.78); g.add(bridge);
      break;
    }
    case "sunglasses": {
      const lensMat = new THREE.MeshStandardMaterial({ color: 0x111827, metalness: 0.2, roughness: 0.2 });
      for (const x of [-0.16, 0.16]) {
        const lens = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.13, 0.04), lensMat);
        lens.position.set(x, 2.13, 0.78); g.add(lens);
      }
      const bridge = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.03, 0.04), lensMat);
      bridge.position.set(0, 2.13, 0.78); g.add(bridge);
      break;
    }
    case "star-shades": {
      const mat = new THREE.MeshStandardMaterial({
        color: 0xa855f7, emissive: 0x6d28d9, emissiveIntensity: 0.25, metalness: 0.3, roughness: 0.3,
      });
      const starShape = new THREE.Shape();
      const pts = 5; const outer = 0.18; const inner = 0.08;
      for (let i = 0; i < pts * 2; i++) {
        const r = i % 2 === 0 ? outer : inner;
        const a = (i / (pts * 2)) * Math.PI * 2 - Math.PI / 2;
        const x = Math.cos(a) * r; const y = Math.sin(a) * r;
        if (i === 0) starShape.moveTo(x, y); else starShape.lineTo(x, y);
      }
      starShape.closePath();
      const geo = new THREE.ExtrudeGeometry(starShape, { depth: 0.05, bevelEnabled: false });
      for (const x of [-0.17, 0.17]) {
        const m = new THREE.Mesh(geo, mat);
        m.position.set(x, 2.13, 0.78); g.add(m);
      }
      break;
    }
    case "monocle": {
      const frameMat = new THREE.MeshStandardMaterial({ color: 0xffd166, metalness: 0.5, roughness: 0.3 });
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.14, 0.03, 8, 18), frameMat);
      ring.rotation.y = Math.PI / 2; ring.position.set(0.17, 2.1, 0.74); g.add(ring);
      // Chain.
      const chain = new THREE.Mesh(new THREE.CylinderGeometry(0.008, 0.008, 0.4, 6), frameMat);
      chain.position.set(0.3, 1.92, 0.32); chain.rotation.z = -0.5; g.add(chain);
      break;
    }
    case "mustache": {
      const mat = new THREE.MeshStandardMaterial({ color: 0x111827, roughness: 0.7 });
      const stash = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.08, 0.06), mat);
      stash.position.set(0, 1.95, 0.74); g.add(stash);
      const left = new THREE.Mesh(new THREE.SphereGeometry(0.07, 10, 8), mat);
      left.position.set(-0.18, 1.95, 0.74); g.add(left);
      const right = new THREE.Mesh(new THREE.SphereGeometry(0.07, 10, 8), mat);
      right.position.set(0.18, 1.95, 0.74); g.add(right);
      break;
    }
    case "eyepatch": {
      const mat = new THREE.MeshStandardMaterial({ color: 0x111827, roughness: 0.7 });
      const patch = new THREE.Mesh(new THREE.CircleGeometry(0.13, 18), mat);
      patch.position.set(0.16, 2.13, 0.74); g.add(patch);
      // Strap across the head.
      const strap = new THREE.Mesh(
        new THREE.TorusGeometry(0.42, 0.018, 6, 24, Math.PI),
        mat,
      );
      strap.position.set(0, 2.2, 0.05); strap.rotation.x = Math.PI / 2; strap.rotation.y = 0.4; g.add(strap);
      break;
    }
  }
  // Apply the depth/render tweak to every mesh in this face group so
  // accessories always draw on top of the head, regardless of which
  // case added them.
  void G; // hint: tweak ALSO applied here for cases that used plain g.add
  g.traverse((o) => { if ((o as THREE.Mesh).isMesh) tweak(o as THREE.Mesh); });
  return g;
}

// ── Internal: procedural shirt pattern (mirrors Player.ts makeShirtMaterial) ─
function makeShirtMaterial(base: THREE.Color, pattern: PatternId | string): THREE.MeshStandardMaterial {
  if (pattern === "solid") {
    return new THREE.MeshStandardMaterial({ color: base, roughness: 0.55, metalness: 0.03 });
  }
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#" + base.getHexString();
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = "#ffffff";
  if (pattern === "stripes") {
    const stripeH = size / 8;
    for (let i = 0; i < 8; i += 2) ctx.fillRect(0, i * stripeH, size, stripeH);
  } else if (pattern === "dots") {
    for (let y = 24; y < size; y += 48) {
      for (let x = 24; x < size; x += 48) {
        ctx.beginPath();
        ctx.arc(x + (Math.floor(y / 48) % 2 === 0 ? 0 : 24), y, 10, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  } else if (pattern === "star") {
    ctx.fillStyle = "#ffd166";
    const drawStar = (cx: number, cy: number, r: number) => {
      ctx.beginPath();
      for (let i = 0; i < 10; i++) {
        const ang = (i / 10) * Math.PI * 2 - Math.PI / 2;
        const rad = i % 2 === 0 ? r : r * 0.45;
        const x = cx + Math.cos(ang) * rad;
        const y = cy + Math.sin(ang) * rad;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fill();
    };
    for (let y = 40; y < size; y += 80) {
      for (let x = 40; x < size; x += 80) {
        drawStar(x + (Math.floor(y / 80) % 2 === 0 ? 0 : 40), y, 22);
      }
    }
  } else if (pattern === "checker") {
    const sq = size / 8;
    for (let r = 0; r < 8; r++) {
      for (let c = 0; c < 8; c++) {
        if ((r + c) % 2 === 0) ctx.fillRect(c * sq, r * sq, sq, sq);
      }
    }
  } else if (pattern === "zigzag") {
    const rowH = size / 8;
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 6;
    for (let y = 0; y < size; y += rowH) {
      ctx.beginPath();
      const flip = ((y / rowH) | 0) % 2 === 0;
      ctx.moveTo(0, y + (flip ? rowH * 0.25 : rowH * 0.75));
      for (let x = 0; x < size; x += 32) {
        const yOff = ((x / 32) % 2 === 0) === flip ? rowH * 0.25 : rowH * 0.75;
        ctx.lineTo(x + 16, y + yOff);
      }
      ctx.stroke();
    }
  } else if (pattern === "hearts") {
    ctx.fillStyle = "#ffffff";
    const drawHeart = (cx: number, cy: number, r: number) => {
      ctx.beginPath();
      ctx.moveTo(cx, cy + r * 0.4);
      ctx.bezierCurveTo(cx - r, cy - r * 0.4, cx - r * 1.1, cy + r * 0.6, cx, cy + r);
      ctx.bezierCurveTo(cx + r * 1.1, cy + r * 0.6, cx + r, cy - r * 0.4, cx, cy + r * 0.4);
      ctx.fill();
    };
    for (let y = 32; y < size; y += 56) {
      for (let x = 32; x < size; x += 56) {
        drawHeart(x + (Math.floor(y / 56) % 2 === 0 ? 0 : 28), y, 14);
      }
    }
  } else if (pattern === "lightning") {
    ctx.fillStyle = "#fde047"; // yellow bolts
    const drawBolt = (cx: number, cy: number, s: number) => {
      ctx.beginPath();
      ctx.moveTo(cx + s * 0.4, cy - s);
      ctx.lineTo(cx - s * 0.2, cy);
      ctx.lineTo(cx + s * 0.1, cy);
      ctx.lineTo(cx - s * 0.4, cy + s);
      ctx.lineTo(cx + s * 0.3, cy + s * 0.1);
      ctx.lineTo(cx, cy + s * 0.1);
      ctx.closePath();
      ctx.fill();
    };
    for (let y = 40; y < size; y += 80) {
      for (let x = 40; x < size; x += 80) {
        drawBolt(x + (Math.floor(y / 80) % 2 === 0 ? 0 : 40), y, 18);
      }
    }
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = (window as unknown as { __wrMaxAnisotropy?: number }).__wrMaxAnisotropy ?? 8;
  return new THREE.MeshStandardMaterial({ map: tex, roughness: 0.55, metalness: 0.03 });
}

/**
 * Adds a Fresnel-style rim light to a MeshStandardMaterial via
 * `onBeforeCompile`. The Fresnel term brightens fragments whose surface
 * normal is nearly perpendicular to the view direction — the visual
 * effect is a soft glow along the silhouette of the mesh. Cheap, no
 * extra render pass needed.
 *
 * intensity: 0 = off, ~1.5 = strong rim. The default 1.4 looks right
 * for the cartoon runner under the warm sun.
 */
function applyFresnelRim(
  material: THREE.MeshStandardMaterial,
  rimColor: THREE.Color,
  intensity: number,
) {
  const rim = { color: { value: rimColor.clone() }, power: { value: 2.6 }, intensity: { value: intensity } };
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uRimColor = rim.color;
    shader.uniforms.uRimPower = rim.power;
    shader.uniforms.uRimIntensity = rim.intensity;
    shader.fragmentShader =
      `uniform vec3 uRimColor;\nuniform float uRimPower;\nuniform float uRimIntensity;\n` +
      shader.fragmentShader.replace(
        "#include <output_fragment>",
        `
        // Fresnel rim — view-space normal vs view direction.
        vec3 viewDirRim = normalize(vViewPosition);
        float fres = pow(1.0 - clamp(dot(normalize(normal), viewDirRim), 0.0, 1.0), uRimPower);
        outgoingLight += uRimColor * fres * uRimIntensity;
        #include <output_fragment>
        `,
      );
  };
  // Ensure the material is recompiled if it was already compiled once.
  material.needsUpdate = true;
}
